import { useEffect, useState } from 'react';
import axios from 'axios';
import { GET_BASE_URL } from '../constants/apiEndpoints';

/**
 * Shared @mention resolution for rendered Lounge comments/replies.
 *
 * Previously every comment/reply component ran its own sequential loop of
 * `/api/v1/getUserId` requests each time a message rendered. This module keeps
 * one session-lifetime cache plus in-flight request dedupe so a username is
 * only ever fetched once per session, no matter how many rows mention it.
 */

const MENTION_TOKEN_PATTERN = /@(\w+)/g;

/** normalized username -> user id (or null when the API had no match) */
const userIdCache = new Map<string, string | null>();
/** normalized username -> in-flight lookup, so duplicates share one request */
const inFlightLookups = new Map<string, Promise<string | null>>();

const normalizeName = (name: string) => name.toLowerCase();

/** Matches the DOMParser round-trip the comment components did inline. */
export function parseCommentHtml(rawHtml: any): string {
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(rawHtml || '', 'text/html');
  return doc.body.innerHTML;
}

export function extractMentionNames(html: string): string[] {
  const matches = html.match(MENTION_TOKEN_PATTERN) ?? [];
  const unique: string[] = [];
  const seen = new Set<string>();

  matches
    .map((match) => match.substring(1))
    .filter((name) => name.length > 0)
    .forEach((name) => {
      const key = normalizeName(name);
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(name);
      }
    });

  return unique;
}

export function buildMentionAnchor(name: string, id: string | number): string {
  return "<a href='/disneyland/user/" + id + "/mypost'>@" + name + '</a>';
}

/**
 * Rewrites every mention that is already cached. Unknown or missed usernames
 * keep their original `@name` text untouched.
 */
export function applyCachedMentions(html: string): string {
  return html.replace(MENTION_TOKEN_PATTERN, (match, name: string) => {
    const id = userIdCache.get(normalizeName(name));
    return id ? buildMentionAnchor(name, id) : match;
  });
}

export function getUnresolvedMentionNames(html: string): string[] {
  return extractMentionNames(html).filter(
    (name) => !userIdCache.has(normalizeName(name))
  );
}

function lookupUserId(name: string): Promise<string | null> {
  const key = normalizeName(name);

  if (userIdCache.has(key)) {
    return Promise.resolve(userIdCache.get(key) ?? null);
  }

  const pending = inFlightLookups.get(key);
  if (pending) {
    return pending;
  }

  const request = axios
    .get(GET_BASE_URL + '/api/v1/getUserId?name=' + encodeURIComponent(name))
    .then((response: any) => {
      const record = response?.data?.data?.[0];
      const id = record?.id != null ? String(record.id) : null;
      userIdCache.set(key, id);
      return id;
    })
    .catch(() => null)
    .finally(() => {
      inFlightLookups.delete(key);
    });

  inFlightLookups.set(key, request);
  return request;
}

/**
 * Resolves all mentions in an already-parsed comment/reply HTML string.
 * Unique usernames are fetched in parallel; misses and errors leave the
 * original `@token` untouched.
 */
export async function resolveMentionHtml(html: string): Promise<string> {
  const unresolved = getUnresolvedMentionNames(html);

  if (unresolved.length > 0) {
    await Promise.all(unresolved.map((name) => lookupUserId(name)));
  }

  return applyCachedMentions(html);
}

/**
 * Comment/reply rendering hook. Returns the mention-resolved HTML for the
 * supplied raw message, applying anything already cached synchronously and
 * dropping late responses that belong to an older message.
 */
export function useResolvedMentionHtml(rawHtml: any): string {
  const [formatedMsg, setFormatedMsg] = useState<string>(() =>
    applyCachedMentions(parseCommentHtml(rawHtml))
  );

  useEffect(() => {
    let cancelled = false;
    const parsed = parseCommentHtml(rawHtml);

    setFormatedMsg(applyCachedMentions(parsed));

    if (getUnresolvedMentionNames(parsed).length > 0) {
      resolveMentionHtml(parsed).then((resolved) => {
        if (!cancelled) {
          setFormatedMsg(resolved);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [rawHtml]);

  return formatedMsg;
}

/** Test-only helper: drops the session cache between test cases. */
export function resetMentionResolverCache(): void {
  userIdCache.clear();
  inFlightLookups.clear();
}
